import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { IBbitWebShopItem } from '@bbitgmbh/bbit.utils';
import { BbitWebShopApiService } from '@bbitgmbh/webshop.sdk';
import { BehaviorSubject, Subject } from 'rxjs';
import { BbitImageOverlayItem, BbitImageOverlayItemType } from '../image-overlay/image-overlay.ref';
import { ImageSliderComponent } from '../image-slider/image-slider.component';
import { LoadingComponent } from '../loading/loading.component';

@Component({
  selector: 'app-item-image',
  templateUrl: './item-image.component.html',
  styleUrls: ['./item-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingComponent, AsyncPipe, ImageSliderComponent],
})
export class ItemImageComponent implements OnInit, OnDestroy, OnChanges {
  private _api = inject(BbitWebShopApiService);
  public isLoading = new BehaviorSubject(true);
  public items = new BehaviorSubject<BbitImageOverlayItem[]>([]);
  private _ngUnsubscribe = new Subject<void>();

  readonly item = input<string>();
  readonly doNotShowLoadingSpinner = input(false);

  ngOnDestroy() {
    this._ngUnsubscribe.next();
    this._ngUnsubscribe.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    const itemChange = changes['item'];
    if (itemChange?.currentValue !== itemChange?.previousValue) {
      this._reload();
    }
  }

  async ngOnInit() {
    await this._reload();
  }

  private async _reload() {
    const item = this.item();
    if (item) {
      this.isLoading.next(true);
      try {
        const selectedItem = await this._api.getItemById(item);
        let mainItem: IBbitWebShopItem | undefined;
        if (selectedItem.itemType === '#productVariant' && selectedItem.productVariantGroup) {
          mainItem = await this._api.getItemById(selectedItem.productVariantGroup);
        }
        const items: BbitImageOverlayItem[] = [];
        const allImages = [...(mainItem?.images || []), ...(selectedItem?.images || [])];
        for (const o of allImages) {
          if (o?.file?.url) {
            items.push({
              type: BbitImageOverlayItemType.IMAGE,
              url: o.file.url,
            });
          }
        }
        const allVideos = [...(mainItem?.videos || []), ...(selectedItem?.videos || [])];
        for (const o of allVideos) {
          if (o?.youTubeId || o?.vimeoId) {
            items.push({
              type: BbitImageOverlayItemType.VIDEO,
              youTubeId: o.youTubeId,
              vimeoId: o.vimeoId,
            });
          }
        }
        this.items.next(items);
      } catch (err) {
        console.error('Error while loading stock', item, err);
        this.items.next([]);
      }
    }
    this.isLoading.next(false);
  }
}
